import { useQuery } from '@tanstack/vue-query'
import { minutesToMs } from '@thyme/libs/src/time/convert'
import { DateTime } from 'luxon'
import { storeToRefs } from 'pinia'
import { computed, toValue, type MaybeRef, type MaybeRefOrGetter } from 'vue'
import {
  UPCOMING_CALLS_ENDPOINT,
  upcomingCallsApi,
} from '@/api/communications/upcomingCalls/api'
import {
  UpcomingCallQueryParams,
  UpcomingCallResponse,
} from '@/api/communications/upcomingCalls/types'
import { dateTimeToString, fromISOWithZone } from '@/libs/date/date'
import { formatName } from '@/libs/format/format'
import { useFlagStore } from '@/store/flags/flags'
import { useWhoAmI } from '@/store/profile/whoAmI'

// The endpoint returns no end time yet, so assume a half hour.
// IMPROVEME(MET-3055) use real endtime once date range work complete
const DEFAULT_CALL_DURATION_MINUTES = 30

const POLL_INTERVAL_MINUTES = 15

type UpcomingCallBase = {
  communicationId: string
  patientId: string
  patientName: string
  dueDate: string
}

export type UpcomingCall = UpcomingCallBase &
  (
    | { isDateOnly: false; startTime: DateTime; endTime: DateTime }
    | { isDateOnly: true }
  )

/**
 * Maps an endpoint row to the shape reminders consume. Update this if the
 * response shape changes — nothing downstream reads the raw response.
 * @param call
 */
export function toUpcomingCall(call: UpcomingCallResponse): UpcomingCall {
  const base: UpcomingCallBase = {
    communicationId: call.communicationId,
    patientId: call.patientId,
    patientName: formatName(
      call.patientDetails?.firstName,
      call.patientDetails?.lastName
    ).trim(),
    dueDate: call.dueDate,
  }

  const dueDatetime = call.dueDatetime
    ? fromISOWithZone(call.dueDatetime)
    : null

  // A missing dueDatetime is the date-only marker going forward. The showTime check
  // is the transitional half: the old write path stores date-only calls at a
  // placeholder dueDatetime, which without it would read as a real scheduled time.
  // CLEANUP(MET-3054): drop the showTime check once due date FF becomes main path
  if (!dueDatetime || !call.showTime) {
    return { ...base, isDateOnly: true }
  }

  const startTime = dueDatetime.toLocal()

  return {
    ...base,
    isDateOnly: false,
    startTime,
    endTime: startTime.plus({ minutes: DEFAULT_CALL_DURATION_MINUTES }),
  }
}

export const upcomingCallsQueryKeys = {
  // 'upcoming' separate key from ScheduledCallsQueue's, since seeded from the same endpoint string
  all: [UPCOMING_CALLS_ENDPOINT, 'upcoming'] as const,
  list: (params: MaybeRef<UpcomingCallQueryParams>) =>
    [...upcomingCallsQueryKeys.all, params] as const,
}

/**
 * Fetches the whole day rather than a look-ahead window to include date-only calls.
 *
 * Which column the day window filters on follows showScheduledCallsDueDate, so the read
 * side matches the write side: with the flag on, date-only calls are written with no
 * dueDatetime and are reachable only through filter_due_date_*.
 * @param day any time on the local day to fetch; normalized to local midnight so
 * the query key stays stable between polls
 */
export function useUpcomingCallsQuery(day: MaybeRefOrGetter<DateTime>) {
  const { showScheduledCallsToast, showScheduledCallsDueDate } =
    storeToRefs(useFlagStore())
  const { selfEntityId } = useWhoAmI()

  // Params are part of the query key, so they change only when the day or the flag does
  const params = computed<UpcomingCallQueryParams>(() => {
    const start = toValue(day).startOf('day')
    return {
      filter_staff_ids: selfEntityId.value ? [selfEntityId.value] : [],
      filter_is_completed: false,
      // CLEANUP(MET-3054): keep only the dueDate branch with the flag
      ...(showScheduledCallsDueDate.value
        ? {
            // Required for the endpoint to accept filter_due_date_*
            // CLEANUP(MET-2988): backend query-param flag switch
            ff_show_planned_calls_due_date: true,
            filter_due_date_after: start.toFormat('yyyy-MM-dd'),
            filter_due_date_before: start
              .plus({ days: 1 })
              .toFormat('yyyy-MM-dd'),
          }
        : {
            filter_due_after: dateTimeToString(start),
            filter_due_before: dateTimeToString(start.plus({ days: 1 })),
          }),
    }
  })

  return useQuery({
    queryKey: upcomingCallsQueryKeys.list(params),
    queryFn: () => upcomingCallsApi.listAll({ params: params.value }),
    // CLEANUP(MET-2986): drop `enabled` with the showScheduledCallsToast flag
    enabled: computed(
      () => showScheduledCallsToast.value && !!selfEntityId.value
    ),
    select: (response) => {
      const calls = response.data.map(toUpcomingCall)
      return calls
    },
    refetchInterval: minutesToMs(POLL_INTERVAL_MINUTES),
    // Keep polling while the tab is unfocused
    refetchIntervalInBackground: true,
  })
}
